import moment from "moment";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import { toast } from "react-toastify";
import apiCall from "../../API/API";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import "./../Rewards/With.css";

const TeamDevelopmentBonus = () => {
  const [rewards, setRewards] = useState({ data: [], totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("none");
  const [search, setSearch] = useState({
    username: "",
    search: "",
    startDate: "",
    endDate: "",
    status: "",
    uid: "",
  });

  const [limitFilter, setLimitFilter] = useState(20);
  const [dateFilter, setDateFilter] = useState("default");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [updatingStatusFor, setUpdatingStatusFor] = useState(null);

  // Form fields for assigning commission
  const [assignUsername, setAssignUsername] = useState("");
  const [commissionPercentage, setCommissionPercentage] = useState("");
  const [usernameValidation, setUsernameValidation] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);

  const buildQueryParams = (overrides = {}) => ({
    page: currentPage,
    limit: limitFilter,
    ...Object.fromEntries(
      Object.entries(search).filter(
        ([_, value]) => value !== "" && value !== 0,
      ),
    ),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...overrides,
  });

  const loadRewards = async (overrides = {}) => {
    const queryParams = buildQueryParams(overrides);
    const endpoint = `/all_leadership_commission_request?${new URLSearchParams(
      queryParams,
    ).toString()}`;
    const { data, error } = await apiCall("get", endpoint);
    if (error) {
      toast.error(error?.message || "Failed to fetch leadership commission");
      return;
    }
    const payload = data?.data ?? data;
    const rewardsList = Array.isArray(payload)
      ? payload
      : payload?.data || payload?.records || payload?.docs || [];
    const pages =
      data?.totalPages ||
      payload?.totalPages ||
      (data?.totalRecords
        ? Math.max(1, Math.ceil(data.totalRecords / limitFilter))
        : 1);
    setRewards({
      data: rewardsList,
      totalPages: pages,
    });
  };

  const toggleFilter = () => {
    if (filter === "none") {
      setFilter("flex");
    } else {
      setFilter("none");
    }
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") {
      setStartDate(value);
    } else if (name === "endDate") {
      setEndDate(value);
    } else {
      setSearch((prev) => {
        const updatedSearch = { ...prev, [name]: value };
        Object.keys(updatedSearch).forEach((key) => {
          if (updatedSearch[key] === "" || updatedSearch[key] === 0) {
            delete updatedSearch[key];
          }
        });
        return updatedSearch;
      });
    }
  };

  const handleLimitChange = (e) => {
    setLimitFilter(e.target.value);
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    const today = moment().endOf("day");
    let start = "";
    let end = "";
    switch (value) {
      case "today":
        start = today.startOf("day").format("YYYY-MM-DD");
        end = today.format("YYYY-MM-DD");
        break;
      case "yesterday":
        start = today.subtract(1, "days").startOf("day").format("YYYY-MM-DD");
        end = today.endOf("day").format("YYYY-MM-DD");
        break;
      case "this_week":
        start = today.startOf("week").format("YYYY-MM-DD");
        end = today.endOf("week").format("YYYY-MM-DD");
        break;
      case "this_month":
        start = today.startOf("month").format("YYYY-MM-DD");
        end = today.endOf("month").format("YYYY-MM-DD");
        break;
      default:
        start = "";
        end = "";
        break;
    }
    setStartDate(start);
    setEndDate(end);
  };

  const addFilter = () => {
    const queryParams = {
      page: currentPage,
      limit: limitFilter,
      ...Object.fromEntries(
        Object.entries(search).filter(
          ([_, value]) => value !== "" && value !== 0,
        ),
      ),
    };

    if (startDate) {
      queryParams.startDate = startDate;
    }
    if (endDate) {
      queryParams.endDate = endDate;
    }

    loadRewards(queryParams);
  };

  const resetFilter = () => {
    setSearch({
      username: "",
      search: "",
      startDate: "",
      endDate: "",
      status: "",
      uid: "",
    });
    setLimitFilter(20);
    setStartDate("");
    setEndDate("");
    setDateFilter("default");
    setCurrentPage(1);

    loadRewards({
      page: 1,
      limit: 20,
    });
  };

  useEffect(() => {
    loadRewards();
  }, [currentPage, limitFilter, search, startDate, endDate]);

  useEffect(() => {
    setTotalPages(rewards?.totalPages || 1);
  }, [rewards]);

  // Check username validation
  const checkUsername = async () => {
    if (!assignUsername) {
      setUsernameValidation("");
      return;
    }

    try {
      const { data, error } = await apiCall("post", "/check_username", {
        username: assignUsername,
      });
      if (data) {
        setUsernameValidation(data.name);
      } else if (error) {
        setUsernameValidation("User not exists");
      }
    } catch (error) {
      setUsernameValidation("User not exists");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      checkUsername();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [assignUsername]);

  // Handle update leadership status
  const handleUpdateStatus = async (username, status) => {
    setUpdatingStatusFor(username);
    try {
      const { data, error } = await apiCall(
        "post",
        "/update_leadership_status",
        {
          username,
          status,
        },
      );
      if (data) {
        toast.success(data?.message || "Status updated successfully");
        loadRewards({
          page: currentPage,
          limit: limitFilter,
          ...Object.fromEntries(
            Object.entries(search).filter(
              ([_, value]) => value !== "" && value !== 0,
            ),
          ),
          ...(startDate && endDate ? { startDate, endDate } : {}),
        });
      } else if (error) {
        toast.error(error?.message || "Failed to update status");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      setUpdatingStatusFor(null);
    }
  };

  // Handle assign commission submission
  const handleAssignCommission = async (e) => {
    e.preventDefault();

    if (!assignUsername || !commissionPercentage) {
      toast.error("Please fill in all fields");
      return;
    }

    if (usernameValidation === "User not exists" || !usernameValidation) {
      toast.error("Please enter a valid username");
      return;
    }

    setAssignLoading(true);

    try {
      const { data, error } = await apiCall(
        "post",
        "/assign_leadership_commission",
        {
          username: assignUsername,
          commission_percentage: commissionPercentage,
        },
      );

      if (data) {
        toast.success(data?.message || "Commission assigned successfully");
        setAssignUsername("");
        setCommissionPercentage("");
        setUsernameValidation("");
        // Refresh the data
        loadRewards({
          page: currentPage,
          limit: limitFilter,
        });
      } else if (error) {
        toast.error(error?.message || "Failed to assign commission");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      setAssignLoading(false);
    }
  };

  const [permissions] = useState({
    setAssignLeadershipCommission: true,
    setLeadershipBenefits: true,
  });

  return (
    <React.Fragment>
      <Container>
        <div className="pagePath">
          <p className="m-0">
            Home / <span>Team Development Bonus</span>
          </p>
        </div>

        {permissions?.setAssignLeadershipCommission && (
          <>
            {/* Assign Commission Form */}
            <Row style={{ marginBottom: "20px", marginTop: "20px" }}>
              <Col md="12">
                <div>
                  <h5 style={{ marginBottom: "15px", textAlign: "center" }}>
                    Assign Team Development Bonus
                  </h5>
                  <Row
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Col lg="6" md="6">
                      <div style={{ marginBottom: "15px" }}>
                        <label
                          style={{ marginBottom: "5px", display: "block" }}
                        >
                          Username
                        </label>
                        <input
                          type="text"
                          placeholder="Enter username"
                          value={assignUsername}
                          onChange={(e) => setAssignUsername(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                        />
                        {usernameValidation && (
                          <small
                            style={{
                              color:
                                usernameValidation === "User not exists"
                                  ? "#dc3545"
                                  : "#28a745",
                              marginTop: "5px",
                              display: "block",
                            }}
                          >
                            {usernameValidation}
                          </small>
                        )}
                      </div>
                    </Col>
                    <Col lg="6" md="6">
                      <div style={{ marginBottom: "15px" }}>
                        <label
                          style={{ marginBottom: "5px", display: "block" }}
                        >
                          Commission Percentage
                        </label>
                        <input
                          type="number"
                          placeholder="Enter commission percentage"
                          value={commissionPercentage}
                          onChange={(e) =>
                            setCommissionPercentage(e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                          }}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                    </Col>
                    <Col lg="4" md="4">
                      <div style={{ marginLeft: "30px" }}>
                        <button
                          className="btn btn-primary"
                          onClick={handleAssignCommission}
                          disabled={assignLoading}
                          style={{
                            opacity: assignLoading ? 0.6 : 1,
                            cursor: assignLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {assignLoading ? "Assigning..." : "Assign"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </>
        )}

        {permissions?.setLeadershipBenefits && (
          <>
            <Row id="filterRow">
              <span id="filterLogo1" onClick={toggleFilter}>
                <i>
                  <BiFilter />
                </i>
                Filter
              </span>

              <Row className="p-0 m-0" style={{ display: filter }}>
                <Col lg="2" md="4">
                  <div>
                    <input
                      type="text"
                      name="search"
                      placeholder="Search By Rank Name"
                      value={search.search}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <input
                      type="text"
                      name="username"
                      placeholder="Search By Username"
                      value={search.username}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <input
                      type="text"
                      placeholder="Search User By Uid"
                      name="uid"
                      value={search.uid}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <select
                      name="status"
                      value={search.status}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                      }}
                    >
                      <option value="">All Status</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <input
                      type="date"
                      name="startDate"
                      placeholder="Start Date"
                      value={startDate}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <input
                      type="date"
                      name="endDate"
                      placeholder="End Date"
                      value={endDate}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <select
                      name="dateFilter"
                      value={dateFilter}
                      onChange={handleDateFilterChange}
                    >
                      <option value="default">Default</option>
                      <option value="today">Today</option>
                      <option value="yesterday">Yesterday</option>
                      <option value="this_week">This Week</option>
                      <option value="this_month">This Month</option>
                    </select>
                  </div>
                </Col>
                <Col lg="2" md="4">
                  <div>
                    <select value={limitFilter} onChange={handleLimitChange}>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="200">200</option>
                    </select>
                  </div>
                </Col>
                <div>
                  <button className="save filter" onClick={addFilter}>
                    Filter{" "}
                    <i>
                      <BiFilter />
                    </i>
                  </button>
                  <button className="cancel reset" onClick={resetFilter}>
                    Reset{" "}
                    <i>
                      <GrPowerReset />
                    </i>
                  </button>
                </div>
              </Row>
            </Row>

            <div id="directIncomeDiv">
              <Row id="directTableRow" className="text-center">
                <Col md="12" className="p-0">
                  <div style={{ width: "100%" }}>
                    <table>
                      <tr>
                        <th>S No.</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Uid</th>
                        <th>Commission Percentage</th>
                        <th>Created Date & Time</th>
                        <th>Status</th>
                        <th>Update Status</th>
                      </tr>
                      {rewards?.data?.map((x, i) => (
                        <tr key={x._id}>
                          <td>{i + 1 + limitFilter * (currentPage - 1)}</td>
                          <td>{x.username}</td>
                          <td>{x.email}</td>
                          <td>{x?.uid}</td>
                          <td>{x.commission_percentage}%</td>
                          <td>
                            {moment(x?.createdAt).format(
                              "MM-DD-YYYY   &   HH:mm",
                            )}
                          </td>
                          <td
                            style={{
                              color: x.status === 1 ? "inherit" : "#dc3545",
                              fontWeight: x.status === 1 ? "normal" : "500",
                            }}
                          >
                            {x.status === 1
                              ? "Active"
                              : x.statusText || "Inactive"}
                          </td>
                          <td>
                            <select
                              value={x.status}
                              onChange={(e) =>
                                handleUpdateStatus(
                                  x.username,
                                  Number(e.target.value),
                                )
                              }
                              disabled={updatingStatusFor === x.username}
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                                minWidth: "90px",
                                opacity:
                                  updatingStatusFor === x.username ? 0.6 : 1,
                              }}
                            >
                              <option value={1}>Active</option>
                              <option value={0}>Inactive</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </table>
                  </div>
                </Col>
              </Row>
            </div>
            <Row>
              <Col md="12">
                {
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePagination}
                  />
                }
              </Col>
            </Row>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default TeamDevelopmentBonus;
